import React, { useState } from 'react';
import { User, Mail, Phone, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Tour } from '../../types/tours';
import PhoneInput from './PhoneInput';
import { useNavigate } from 'react-router-dom';

interface BookingContactStepProps {
  tour: Tour;
  data: {
    date: Date | null;
    groupType: 'group' | 'reduced' | 'private' | null;
    persons: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    pickupAddress: string;
    totalPrice: number;
    paymentMethod?: 'cash' | 'paypal';
    bookingCode?: string;
  };
  onChange: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    bookingCode?: string;
  }) => void;
  onBack: () => void;
  onNext: () => void;
}

async function createBooking(data: BookingContactStepProps['data'], tour: Tour) {
  try {
    if (!tour) {
      throw new Error('Tour data is missing');
    }
    
    if (!tour.id) {
      throw new Error('Tour ID is missing');
    }

    if (!data.date) {
      throw new Error('Booking date is required');
    }

    if (!data.groupType) {
      throw new Error('Group type is required');
    }

    if (!data.persons || data.persons < 1) {
      throw new Error('Number of persons is required');
    }

    if (data.groupType === 'group' && data.persons > 10) {
      throw new Error('Group tours are limited to 10 persons');
    }
    
    if (data.groupType === 'reduced' && data.persons > 6) {
      throw new Error('Reduced group tours are limited to 6 persons');
    }

    // Prepare booking data
    const bookingData = {
      tour_id: tour.id,
      booking_date: data.date.toISOString().split('T')[0],
      group_type: data.groupType,
      persons: data.persons,
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      email: data.email.trim().toLowerCase(),
      phone: data.phone.trim(),
      pickup_address: 'Las Vegas Restaurant, Sierpe',
      total_price: Number(data.totalPrice.toFixed(2)),
      status: 'confirmed'
    } as const;

    const { data: booking, error } = await supabase
      .from('bookings')
      .insert(bookingData)
      .select()
      .single();

    if (error) {
      console.error('Supabase error:', error);
      if (error.code === '23503') {
        throw new Error('Invalid tour selected');
      } else if (error.code === '23514') {
        throw new Error('Invalid booking data provided');
      } else {
        throw error;
      }
    }

    if (!booking) {
      throw new Error('No booking data returned');
    }

    return booking;
  } catch (err) {
    console.error('Error creating booking:', err);
    throw err;
  }
}

const BookingContactStep: React.FC<BookingContactStepProps> = ({
  tour,
  data,
  onChange,
  onBack,
  onNext
}) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    try {
      if (!data.firstName.trim()) {
        setError('First name is required');
        return;
      }
      if (!data.lastName.trim()) {
        setError('Last name is required');
        return;
      }
      if (!data.email.trim()) {
        setError('Email is required');
        return;
      }
      if (!data.phone.trim()) {
        setError('Phone number is required');
        return;
      }
      
      // Validate boot sizes if rental boots are required
      if (tour.has_rental_boots) {
        const bootSizes = data.bootSizes || [];
        if (bootSizes.length !== data.persons) {
          setError('Please select boot sizes for all participants');
          return;
        }
        if (bootSizes.some(size => !size || size.trim() === '')) {
          setError('Please select boot sizes for all participants');
          return;
        }
      }

      // Generate booking code and store it in the booking data
      const bookingCode = `SF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
      
      // Update the booking data with the booking code
      onChange({ 
        ...data, 
        bookingCode 
      });

      onNext();
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-[#2A382A] mb-2">Contact Information</h2>
        <p className="text-[#2A382A]/70">
          Please provide your contact details and pickup location.
        </p>
      </div>

      <div className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-[#2A382A]/70 mb-2">
              First Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2A382A]/40" />
              <input
                type="text"
                id="firstName"
                name="firstName"
                autoFocus
                value={data.firstName}
                onChange={(e) => onChange({ ...data, firstName: e.target.value })}
                className="w-full pl-12 pr-4 py-3 bg-[#F2F3F4] border border-[#2A382A]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-[#2A382A]"
                placeholder="John"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-[#2A382A]/70 mb-2">
              Last Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2A382A]/40" />
              <input
                type="text"
                id="lastName"
                value={data.lastName}
                onChange={(e) => onChange({ ...data, lastName: e.target.value })}
                className="w-full pl-12 pr-4 py-3 bg-[#F2F3F4] border border-[#2A382A]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-[#2A382A]"
                placeholder="Doe"
                required
              />
            </div>
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[#2A382A]/70 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2A382A]/40" />
            <input
              type="email"
              id="email"
              value={data.email}
              onChange={(e) => onChange({ ...data, email: e.target.value })}
              className="w-full pl-12 pr-4 py-3 bg-[#F2F3F4] border border-[#2A382A]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-[#2A382A]"
              placeholder="john.doe@example.com"
              required
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-[#2A382A]/70 mb-2">
            Phone Number
          </label>
          <PhoneInput
            value={data.phone}
            onChange={(value) => onChange({ ...data, phone: value })}
          />
        </div>
      </div>

      {/* Boot Sizes Selection */}
      {tour.has_rental_boots && (
        <div className="bg-[#F2F3F4] rounded-xl p-6">
          <h3 className="font-semibold text-[#2A382A] mb-4">Rubber Boot Sizes</h3>
          <p className="text-sm text-[#2A382A]/70 mb-4">
            Please select the boot size for each participant. We provide rubber boots for the tour.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: data.persons }, (_, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-[#2A382A]/70 mb-2">
                  Participant {index + 1} Boot Size
                </label>
                <select
                  value={data.bootSizes?.[index] || ''}
                  onChange={(e) => {
                    const newBootSizes = [...(data.bootSizes || [])];
                    newBootSizes[index] = e.target.value;
                    onChange({ ...data, bootSizes: newBootSizes });
                  }}
                  className="w-full px-4 py-3 bg-white border border-[#2A382A]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-[#2A382A]"
                  required
                >
                  <option value="">Select size</option>
                  <option value="35">35 (US 5)</option>
                  <option value="36">36 (US 6)</option>
                  <option value="37">37 (US 6.5)</option>
                  <option value="38">38 (US 7)</option>
                  <option value="39">39 (US 7.5)</option>
                  <option value="40">40 (US 8)</option>
                  <option value="41">41 (US 8.5)</option>
                  <option value="42">42 (US 9)</option>
                  <option value="43">43 (US 9.5)</option>
                  <option value="44">44 (US 10)</option>
                  <option value="45">45 (US 11)</option>
                  <option value="46">46 (US 12)</option>
                  <option value="47">47 (US 13)</option>
                  <option value="48">48 (US 14)</option>
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-white border border-[#2A382A]/10 text-[#2A382A] rounded-lg hover:bg-[#2A382A]/5 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="px-6 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors border-2 border-[#E85C0D]"
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default BookingContactStep;